import { confirmedAdminLogout } from './confirmedAdminLogout.js';
/**
 * Admin Authentication Service
 * Handles admin login, logout, and session management
 */

const API_BASE = '/api/v1';
const DEV_AUTH_HEADERS =
  typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV
    ? {
        'X-DEV-IS-SUPERADMIN': 'true',
        'X-DEV-USER-ID': '00000000-0000-0000-0000-000000000001',
        'X-Test-Mode': 'true',
      }
    : {};

// RUNTIME API BASE LOGGING (always log on first call)
if (typeof window !== 'undefined' && !window.__ADMIN_AUTH_API_BASE_LOGGED) {
  window.__ADMIN_AUTH_API_BASE_LOGGED = true;
  const origin = window.location.origin;
  const fullUrl = `${origin}${API_BASE}/admin/auth/login`;
  console.log('[ADMIN AUTH] API_BASE=', API_BASE);
  console.log('[ADMIN AUTH] ORIGIN=', origin);
  console.log('[ADMIN AUTH] Full URL would be=', fullUrl);
}

/**
 * Login with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{actorId: string, email: string, isSuperAdmin: boolean, capabilities: string[]}>}
 */
export async function adminLogin(email, password) {
  const response = await fetch(`${API_BASE}/admin/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies (session)
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err = new Error(data.message || 'Login failed');
    err.status = response.status;
    err.errorCode = data.errorCode;
    err.details = data.details;
    throw err;
  }

  const data = await response.json();
  return data.data;
}

/**
 * Logout (destroy session)
 * @returns {Promise<void>}
 */
export async function adminLogout() {
  await confirmedAdminLogout();
}

/**
 * Get current admin user info
 * @returns {Promise<{actorId: string, email: string, isSuperAdmin: boolean, mustResetPassword?: boolean, capabilities: string[]}>}
 */
export async function getCurrentAdmin() {
  const response = await fetch(`${API_BASE}/admin/auth/me`, {
    method: 'GET',
    headers: DEV_AUTH_HEADERS,
    credentials: 'include', // Include cookies (session)
  });

  if (!response.ok) {
    if (response.status === 401) {
      return null; // Not authenticated
    }
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to get current user');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Set user's own password (self-service, after forced reset)
 * @param {string} password
 * @returns {Promise<{message: string}>}
 */
export async function setPassword(password) {
  const response = await fetch(`${API_BASE}/admin/auth/set-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies (session)
    body: JSON.stringify({ password }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to set password');
  }

  const data = await response.json();
  return data.data;
}
